import {
  BioComplianceStatusValueType,
  BioSampleCode,
  BioSampleCriteria,
  BioSampleId,
  BioSampleKitCode,
  BioSamplePageQuery,
  BioSampleRefBase,
  BioSampleRefType,
  BioSampleStatusCriterion,
  BioSampleStatusType,
  BioSampleStatusValueType,
  CollectionStatusValueType,
  AnySampleStatusValueType,
  ProcessingStatusValueType,
  ShippingStatusValueType,
} from '@csp/csp-common-hbs-model';
import {
  BioSampleCriteriaFieldsV1,
  BioSampleDateRangeFieldsV1,
  BioSampleQueryCriterionV1,
  BioSampleQueryDateRangeCriterionV1,
  BioSampleQueryOperatorV1,
  BioSampleQuerySortOrderV1,
  BioSampleQuerySortV1,
  BioSampleQueryTypeV1,
  BioSampleQueryV1,
  BioSampleSortFieldsV1,
} from '@csp/dmdp-api-hbs-dto';
import { CspError, CspErrorType, FreeTextCriteria, Maybe, UserId, VisitId } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { ScheduleRequestRef } from '@csp/csp-common-scheduling';
import { groupBy, uniq } from 'lodash';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { BioSampleContainerRefBase } from '@csp/csp-common-hbs-model/src/lib/model/BioSampleContainerRefBase';
import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { SampleSortOrderType } from '@csp/csp-common-hbs-model/src/lib/type/SampleSortOrderType';

// eslint-disable-next-line @typescript-eslint/ban-types
const allEnumValuesExceptUnknown = (enu: {}): string[] =>
  EnumUtil.asStrings(enu).filter(val => val !== BioSampleStatusValueType.UNKNOWN);

const toDateRangeCriteriaV1 = ({ createdIsoDateFromIncl, createdIsoDateToIncl }: Maybe<BioSampleCriteria> = {}): Maybe<
  BioSampleQueryDateRangeCriterionV1[]
> => {
  const v1s: BioSampleQueryDateRangeCriterionV1[] = [];
  if (createdIsoDateFromIncl || createdIsoDateToIncl) {
    v1s.push({
      field: BioSampleDateRangeFieldsV1.CREATE_DATE,
      fromInclusive: true,
      from: createdIsoDateFromIncl,
      toInclusive: true,
      to: createdIsoDateToIncl,
    });
  }
  return v1s.length ? v1s : undefined;
};

const toCurrentBioSampleStatusCriterion = (
  type: BioSampleStatusType,
  values: string[],
  operatorType: BioSampleQueryTypeV1.IN | BioSampleQueryTypeV1.NOT_IN = BioSampleQueryTypeV1.IN,
): BioSampleStatusCriterion => ({
  type,
  operatorType,
  values,
});

const toSampleContainerRefCriterion = (sampleContainerRef: BioSampleContainerRefBase): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.SAMPLE_KIT_CODE,
        type: BioSampleQueryTypeV1.EQ,
        values: [sampleContainerRef.kitCode],
      },
    },
    /* TODO [Yoshi] - enable when supported in BE, https://confluence.astrazeneca.com/x/v4XcH
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.CONTAINER_NUMBER,
        type: BioSampleQueryTypeV1.EQ,
        values: [sampleContainerRef.containerNumber],
      },
    }, */
  ],
});

const toSampleContainerRefsCriterion = (
  sampleContainerRefs: BioSampleContainerRefBase[] = [],
): Maybe<BioSampleQueryCriterionV1> => {
  const criteria = sampleContainerRefs.map(toSampleContainerRefCriterion);
  return criteria.length
    ? {
        operator: BioSampleQueryOperatorV1.OR,
        criteria,
      }
    : undefined;
};

const toSampleCodesCriterion = (sampleCodes: BioSampleCode[] = []): Maybe<BioSampleQueryCriterionV1> =>
  sampleCodes.length
    ? {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.SAMPLE_CODE,
          type: BioSampleQueryTypeV1.IN,
          values: sampleCodes,
        },
      }
    : undefined;

const toSampleRefsCriterion = (sampleRefs: BioSampleRefBase[] = []): Maybe<BioSampleQueryCriterionV1> =>
  sampleRefs.length
    ? {
        operator: BioSampleQueryOperatorV1.OR,
        criteria: [
          toSampleCodesCriterion(sampleRefs.map(sampleRef => sampleRef.sampleCode)),
          toSampleContainerRefsCriterion(sampleRefs),
        ].filter(isDefined),
      }
    : undefined;

const toKitCodesCriterion = (kitCodes: BioSampleKitCode[] = []): Maybe<BioSampleQueryCriterionV1> =>
  kitCodes.length
    ? {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.SAMPLE_KIT_CODE,
          type: BioSampleQueryTypeV1.IN,
          values: kitCodes,
        },
      }
    : undefined;

const toExcludedSampleCodesCriterion = (excludedSampleCodes: BioSampleCode[] = []): Maybe<BioSampleQueryCriterionV1> =>
  excludedSampleCodes.length
    ? {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.SAMPLE_CODE,
          type: BioSampleQueryTypeV1.NOT_IN,
          values: excludedSampleCodes,
        },
      }
    : undefined;

const toPatientIdsCriterion = (patientIds: UserId[] = []): Maybe<BioSampleQueryCriterionV1> =>
  patientIds.length
    ? {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.USER_ID,
          type: BioSampleQueryTypeV1.IN,
          values: patientIds,
        },
      }
    : undefined;

const toCreatedByUserIdsCriterion = (userIds: UserId[] = []): Maybe<BioSampleQueryCriterionV1> =>
  userIds.length
    ? {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.CREATED_BY,
          type: BioSampleQueryTypeV1.IN,
          values: userIds,
        },
      }
    : undefined;

const toVisitIdsCriterion = (visitIds: VisitId[] = []): Maybe<BioSampleQueryCriterionV1> =>
  visitIds.length
    ? {
        operator: BioSampleQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: BioSampleCriteriaFieldsV1.REFS_KEY,
              type: BioSampleQueryTypeV1.EQ,
              values: [BioSampleRefType.VISIT_ID],
            },
          },
          {
            fieldCriterion: {
              field: BioSampleCriteriaFieldsV1.REFS_VALUE,
              type: BioSampleQueryTypeV1.IN,
              values: visitIds,
            },
          },
        ],
      }
    : undefined;

const toAliquotParentIdsCriterion = (aliquotParentIds: BioSampleId[] = []): Maybe<BioSampleQueryCriterionV1> =>
  aliquotParentIds.length
    ? {
        operator: BioSampleQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: BioSampleCriteriaFieldsV1.REFS_KEY,
              type: BioSampleQueryTypeV1.EQ,
              values: [BioSampleRefType.ALIQUOT_PARENT_ID],
            },
          },
          {
            fieldCriterion: {
              field: BioSampleCriteriaFieldsV1.REFS_VALUE,
              type: BioSampleQueryTypeV1.IN,
              values: aliquotParentIds,
            },
          },
        ],
      }
    : undefined;

const toCurrentCustomStatusCriterion = ({
  type,
  operatorType,
  values,
}: BioSampleStatusCriterion): BioSampleQueryCriterionV1 => {
  const typeAndValuesCriterion = {
    operator: BioSampleQueryOperatorV1.AND,
    criteria: [
      {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_TYPE,
          type: BioSampleQueryTypeV1.EQ,
          values: [type],
        },
      },
      {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_VALUE,
          type: operatorType,
          values,
        },
      },
    ],
  };

  if (operatorType === BioSampleQueryTypeV1.IN) {
    return typeAndValuesCriterion;
  } else if (operatorType === BioSampleQueryTypeV1.NOT_IN) {
    return {
      operator: BioSampleQueryOperatorV1.OR,
      criteria: [
        {
          fieldCriterion: {
            field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_TYPE,
            type: BioSampleQueryTypeV1.NOT_IN,
            values: [type],
          },
        },
        typeAndValuesCriterion,
      ],
    };
  } else {
    throw new CspError(
      CspErrorType.BAD_REQUEST,
      `Unsupported operator type for custom status criterion. Type: ${type}, operator: ${operatorType}`,
    );
  }
};

const toCurrentCustomStatusCriteria = (currentStatuses: BioSampleStatusCriterion[] = []): BioSampleQueryCriterionV1[] =>
  currentStatuses.filter(currentStatus => currentStatus.values.length).map(toCurrentCustomStatusCriterion);

const createCustomStatusTypeFieldCriterion = (
  values: BioSampleStatusType,
  type: BioSampleQueryTypeV1 = BioSampleQueryTypeV1.EQ,
): BioSampleQueryCriterionV1 => ({
  fieldCriterion: {
    field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_TYPE,
    type,
    values: [values],
  },
});

const createCustomStatusValuesFieldCriterion = (
  values: AnySampleStatusValueType[],
  type: BioSampleQueryTypeV1.IN | BioSampleQueryTypeV1.NOT_IN = BioSampleQueryTypeV1.IN,
): BioSampleQueryCriterionV1 => ({
  fieldCriterion: {
    field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_VALUE,
    type,
    values,
  },
});

const toCollectedSampleCriteria = (values: AnySampleStatusValueType[]): Maybe<BioSampleQueryCriterionV1> => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: toCurrentCustomStatusCriteria([
    toCurrentBioSampleStatusCriterion(BioSampleStatusType.COLLECTION, values),
    toCurrentBioSampleStatusCriterion(
      BioSampleStatusType.PROCESSING,
      allEnumValuesExceptUnknown(ProcessingStatusValueType),
      BioSampleQueryTypeV1.NOT_IN,
    ),
    toCurrentBioSampleStatusCriterion(
      BioSampleStatusType.SHIPPING,
      allEnumValuesExceptUnknown(ShippingStatusValueType),
      BioSampleQueryTypeV1.NOT_IN,
    ),
  ]),
});

const toProcessedSampleCriteria = (): Maybe<BioSampleQueryCriterionV1> => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: toCurrentCustomStatusCriteria([
    toCurrentBioSampleStatusCriterion(
      BioSampleStatusType.PROCESSING,
      allEnumValuesExceptUnknown(ProcessingStatusValueType),
    ),
    toCurrentBioSampleStatusCriterion(
      BioSampleStatusType.SHIPPING,
      allEnumValuesExceptUnknown(ShippingStatusValueType),
      BioSampleQueryTypeV1.NOT_IN,
    ),
  ]),
});

const toShippedSampleCriteria = (values: ShippingStatusValueType[]): Maybe<BioSampleQueryCriterionV1> => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: toCurrentCustomStatusCriteria([toCurrentBioSampleStatusCriterion(BioSampleStatusType.SHIPPING, values)]),
});

const toCurrentSampleStatusCriterion = (
  sampleStatusCriteria: AnySampleStatusValueType,
): Maybe<BioSampleQueryCriterionV1> => {
  switch (sampleStatusCriteria) {
    case CollectionStatusValueType.COLLECTED:
      return toCollectedSampleCriteria([CollectionStatusValueType.COLLECTED]);
    case CollectionStatusValueType.ALIQUOTED:
      return toCollectedSampleCriteria([CollectionStatusValueType.ALIQUOTED]);
    case CollectionStatusValueType.NOT_COLLECTED:
      return toCollectedSampleCriteria([CollectionStatusValueType.NOT_COLLECTED]);
    case ProcessingStatusValueType.IN_PROCESS:
    case ProcessingStatusValueType.PROCESSED:
      return toProcessedSampleCriteria();
    case ShippingStatusValueType.SHIPPED:
      return toShippedSampleCriteria([ShippingStatusValueType.SHIPPED]);
    case ShippingStatusValueType.USED:
      return toShippedSampleCriteria([ShippingStatusValueType.USED]);
    default:
      return undefined;
  }
};

const toCurrentSampleStatusesCriterion = (
  sampleStatusesCriteria: Maybe<AnySampleStatusValueType[]>,
): Maybe<BioSampleQueryCriterionV1> => {
  const criteria = sampleStatusesCriteria
    ?.map(customStatusCriterion => toCurrentSampleStatusCriterion(customStatusCriterion))
    .filter(isDefined);

  return criteria
    ? {
        operator: BioSampleQueryOperatorV1.OR,
        criteria,
      }
    : undefined;
};

const toSampleCodeKitCodeFreeTextCriterion = (freeText: Maybe<FreeTextCriteria>): Maybe<BioSampleQueryCriterionV1> => {
  const text = freeText?.text?.trim() || undefined;
  const criteria: BioSampleQueryCriterionV1[] = [
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.SAMPLE_CODE,
        type: BioSampleQueryTypeV1.LIKE,
        values: [text],
      },
    },
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.SAMPLE_KIT_CODE,
        type: BioSampleQueryTypeV1.LIKE,
        values: [text],
      },
    },
  ];
  return text
    ? {
        operator: BioSampleQueryOperatorV1.OR,
        criteria,
      }
    : undefined;
};

const toShippingNumberFreeTextCriterion = (freeText: Maybe<FreeTextCriteria>): Maybe<BioSampleQueryCriterionV1> => {
  const text = freeText?.text?.trim() || undefined;
  const criteria: BioSampleQueryCriterionV1[] = [
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_TYPE,
        type: BioSampleQueryTypeV1.EQ,
        values: [BioSampleStatusType.SHIPPING_NUMBER],
      },
    },
    {
      fieldCriterion: {
        field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_VALUE,
        type: BioSampleQueryTypeV1.LIKE,
        values: [text],
      },
    },
  ];
  return text
    ? {
        operator: BioSampleQueryOperatorV1.AND,
        criteria,
      }
    : undefined;
};

const shippableCriteria: BioSampleStatusCriterion[] = [
  toCurrentBioSampleStatusCriterion(
    BioSampleStatusType.SHIPPING,
    allEnumValuesExceptUnknown(ShippingStatusValueType),
    BioSampleQueryTypeV1.NOT_IN,
  ),
  toCurrentBioSampleStatusCriterion(
    BioSampleStatusType.COLLECTION,
    [CollectionStatusValueType.COLLECTED, CollectionStatusValueType.ALIQUOTED],
    BioSampleQueryTypeV1.IN,
  ),
];

const toComplianceStatusesCriterion = (
  type: BioSampleStatusType,
  values: Maybe<BioComplianceStatusValueType[]>,
): Maybe<BioSampleStatusCriterion> =>
  values?.length ? toCurrentBioSampleStatusCriterion(type, values, BioSampleQueryTypeV1.IN) : undefined;

const toCustomStatusCreatedByCriterion = (
  type: BioSampleStatusType,
  values: AnySampleStatusValueType[],
  userIds: UserId[] = [],
): Maybe<BioSampleQueryCriterionV1> => {
  const criteria =
    values.length && userIds.length
      ? [
          createCustomStatusTypeFieldCriterion(type),
          createCustomStatusValuesFieldCriterion(values, BioSampleQueryTypeV1.IN),
          {
            fieldCriterion: {
              field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_CREATED_BY,
              type: BioSampleQueryTypeV1.IN,
              values: userIds,
            },
          },
        ]
      : undefined;
  return criteria
    ? {
        criteria,
        operator: BioSampleQueryOperatorV1.AND,
      }
    : undefined;
};

const toScheduleRefCriterion = (visitRequestRefs: Maybe<ScheduleRequestRef[]>): Maybe<BioSampleQueryCriterionV1> => {
  const groupByScheduleCode = groupBy(visitRequestRefs, ref => ref.scheduleCode);

  const criteriaGroups = Object.entries(groupByScheduleCode).map(([key, scheduleRequestRef]) => ({
    scheduleCode: key,
    requestCodes: uniq(scheduleRequestRef.map(ref => ref.requestCode)),
  }));

  const criteria = criteriaGroups.map(scheduleRef => ({
    operator: BioSampleQueryOperatorV1.AND,
    criteria: [
      {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.SCHEDULE_CODE,
          type: BioSampleQueryTypeV1.EQ,
          values: [scheduleRef.scheduleCode],
        },
      },
      {
        fieldCriterion: {
          field: BioSampleCriteriaFieldsV1.SCHEDULE_REQUEST_CODE,
          type: BioSampleQueryTypeV1.IN,
          values: scheduleRef.requestCodes,
        },
      },
    ],
  }));

  return visitRequestRefs?.length
    ? {
        criteria,
        operator: BioSampleQueryOperatorV1.OR,
      }
    : undefined;
};

const notShippedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.OR,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.SHIPPING, BioSampleQueryTypeV1.NOT_IN),
    {
      operator: BioSampleQueryOperatorV1.AND,
      criteria: [
        createCustomStatusTypeFieldCriterion(BioSampleStatusType.SHIPPING, BioSampleQueryTypeV1.EQ),
        {
          fieldCriterion: {
            field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_VALUE,
            type: BioSampleQueryTypeV1.NOT_IN,
            values: allEnumValuesExceptUnknown(ShippingStatusValueType),
          },
        },
      ],
    },
  ],
});

const notProcessedOrInProcessingCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.OR,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.PROCESSING, BioSampleQueryTypeV1.NOT_IN),
    {
      operator: BioSampleQueryOperatorV1.AND,
      criteria: [
        createCustomStatusTypeFieldCriterion(BioSampleStatusType.PROCESSING, BioSampleQueryTypeV1.EQ),
        {
          operator: BioSampleQueryOperatorV1.OR,
          criteria: [
            {
              operator: BioSampleQueryOperatorV1.AND,
              criteria: [
                createCustomStatusValuesFieldCriterion([ProcessingStatusValueType.IN_PROCESS], BioSampleQueryTypeV1.IN),
                createCustomStatusValuesFieldCriterion(
                  [ProcessingStatusValueType.PROCESSED],
                  BioSampleQueryTypeV1.NOT_IN,
                ),
              ],
            },
            {
              operator: BioSampleQueryOperatorV1.AND,
              criteria: [
                createCustomStatusValuesFieldCriterion([ProcessingStatusValueType.PROCESSED], BioSampleQueryTypeV1.IN),
                {
                  fieldCriterion: {
                    field: BioSampleCriteriaFieldsV1.CUSTOM_STATUS_LOCAL_TIMESTAMP,
                    type: BioSampleQueryTypeV1.GREATER_THAN,
                    values: [ZonedDateTimeFormatter.toIsoFormattedString(ZonedDateTimeFactory.now())],
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

const collectedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.COLLECTION, BioSampleQueryTypeV1.EQ),
    createCustomStatusValuesFieldCriterion([CollectionStatusValueType.COLLECTED], BioSampleQueryTypeV1.IN),
  ],
});

const toProcessableCriterion = (processable: Maybe<boolean>): Maybe<BioSampleQueryCriterionV1> =>
  processable
    ? {
        operator: BioSampleQueryOperatorV1.AND,
        criteria: [notShippedCriteria(), collectedCriteria(), notProcessedOrInProcessingCriteria()],
      }
    : undefined;

const isInProcessOrProcessedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.PROCESSING, BioSampleQueryTypeV1.EQ),
    createCustomStatusValuesFieldCriterion(
      [ProcessingStatusValueType.IN_PROCESS, ProcessingStatusValueType.PROCESSED],
      BioSampleQueryTypeV1.IN,
    ),
  ],
});

const isShippedOrUsedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.SHIPPING, BioSampleQueryTypeV1.EQ),
    createCustomStatusValuesFieldCriterion(
      [ShippingStatusValueType.USED, ShippingStatusValueType.SHIPPED],
      BioSampleQueryTypeV1.IN,
    ),
  ],
});

const isNotAliquotedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.COLLECTION, BioSampleQueryTypeV1.EQ),
    createCustomStatusValuesFieldCriterion([CollectionStatusValueType.ALIQUOTED], BioSampleQueryTypeV1.NOT_IN),
  ],
});

const isNotNotCollectedCriteria = (): BioSampleQueryCriterionV1 => ({
  operator: BioSampleQueryOperatorV1.AND,
  criteria: [
    createCustomStatusTypeFieldCriterion(BioSampleStatusType.COLLECTION, BioSampleQueryTypeV1.EQ),
    createCustomStatusValuesFieldCriterion([CollectionStatusValueType.NOT_COLLECTED], BioSampleQueryTypeV1.NOT_IN),
  ],
});

const toMultiAliquotableCriterion = (multiAliquotable: Maybe<boolean>): Maybe<BioSampleQueryCriterionV1> =>
  multiAliquotable
    ? {
        operator: BioSampleQueryOperatorV1.AND,
        criteria: [
          {
            operator: BioSampleQueryOperatorV1.OR,
            criteria: [collectedCriteria(), isInProcessOrProcessedCriteria(), isShippedOrUsedCriteria()],
          },
          isNotAliquotedCriteria(),
          isNotNotCollectedCriteria(),
        ],
      }
    : undefined;

const toQueryV1 = ({ fields = [], sort = defaultSorting, criteria }: BioSamplePageQuery): BioSampleQueryV1 => {
  const criteriaV1: Array<Maybe<BioSampleQueryCriterionV1>> = [];
  criteriaV1.push(toSampleCodesCriterion(criteria?.sampleCodes));
  criteriaV1.push(toSampleRefsCriterion(criteria?.sampleRefs));
  criteriaV1.push(toSampleContainerRefsCriterion(criteria?.containerRefs));
  criteriaV1.push(toKitCodesCriterion(criteria?.kitCodes));
  criteriaV1.push(toExcludedSampleCodesCriterion(criteria?.excludedSampleCodes));
  criteriaV1.push(toPatientIdsCriterion(criteria?.patientIds));
  criteriaV1.push(toPatientIdsCriterion(criteria?.orgIdPatientIds));
  criteriaV1.push(toSampleCodeKitCodeFreeTextCriterion(criteria?.sampleCodeKitCode));
  criteriaV1.push(toShippingNumberFreeTextCriterion(criteria?.shippingNumber));
  criteriaV1.push(toScheduleRefCriterion(criteria?.visitRequestRefs));
  criteriaV1.push(toCurrentSampleStatusesCriterion(criteria?.currentSampleStatuses));
  criteriaV1.push(toCreatedByUserIdsCriterion(criteria?.createdByUserIds));
  criteriaV1.push(toVisitIdsCriterion(criteria?.visitIds));
  criteriaV1.push(toAliquotParentIdsCriterion(criteria?.aliquotParentIds));
  criteriaV1.push(
    toCustomStatusCreatedByCriterion(
      BioSampleStatusType.SHIPPING,
      [ShippingStatusValueType.SHIPPED],
      criteria?.shippedByUserIds,
    ),
  );
  criteriaV1.push(
    toCustomStatusCreatedByCriterion(
      BioSampleStatusType.PROCESSING,
      [ProcessingStatusValueType.PROCESSED, ProcessingStatusValueType.IN_PROCESS],
      criteria?.processedByUserIds,
    ),
  );
  criteriaV1.push(toProcessableCriterion(criteria?.processable));
  criteriaV1.push(toMultiAliquotableCriterion(criteria?.multiAliquotable));

  criteriaV1.push(
    ...toCurrentCustomStatusCriteria(
      [
        ...(criteria?.currentStatuses ?? []),
        ...(criteria?.shippable ? shippableCriteria : []),
        toComplianceStatusesCriterion(BioSampleStatusType.PROCESS_COMPLIANCE, criteria?.processComplianceStatuses),
        toComplianceStatusesCriterion(BioSampleStatusType.STORAGE_COMPLIANCE, criteria?.storageComplianceStatuses),
      ].filter(isDefined),
    ),
  );

  const criterionV1: BioSampleQueryCriterionV1 = {
    operator: BioSampleQueryOperatorV1.AND,
    criteria: criteriaV1.filter(isDefined),
  };

  return {
    fields,
    dateRangeCriteria: toDateRangeCriteriaV1(criteria),
    criterion: criterionV1.criteria?.length ? criterionV1 : undefined,
    sort: toSorting(sort),
  };
};

const toSorting = (sort: SampleSortOrderType): BioSampleQuerySortV1[] => {
  switch (sort) {
    case SampleSortOrderType.NEWEST_FIRST:
      return [
        {
          field: BioSampleSortFieldsV1.LAST_MODIFIED,
          order: BioSampleQuerySortOrderV1.DESC,
        },
      ];
    case SampleSortOrderType.SAMPLE_CODE:
      return [
        {
          field: BioSampleSortFieldsV1.SAMPLE_CODE,
          order: BioSampleQuerySortOrderV1.ASC,
        },
      ];
    default:
      return [
        {
          field: BioSampleSortFieldsV1.CREATE_DATE,
          order: BioSampleQuerySortOrderV1.DESC,
        },
        {
          field: BioSampleSortFieldsV1.SAMPLE_CODE,
          order: BioSampleQuerySortOrderV1.ASC,
        },
      ];
  }
};

const defaultSorting = SampleSortOrderType.LAST_CREATED_AND_SAMPLE_CODE;

const fromQuery = (partial: Partial<BioSamplePageQuery>): BioSamplePageQuery => ({
  pageSize: 50,
  count: false,
  sort: defaultSorting,
  ...partial,
});

const fromCriteria = (criteria: Maybe<BioSampleCriteria>): BioSamplePageQuery =>
  fromQuery({
    criteria,
  });

export const BioSampleQueryMapper = {
  toQueryV1,
  fromCriteria,
  fromQuery,
  defaultSorting,
};
